import React, { useState, useEffect } from 'react';
import { Filter, ChevronLeft, Search, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Reusable Filter Sidebar Component
 * Desktop (md+): collapsible side panel driven by isOpen/onToggle.
 * Mobile (<md): self-contained — a floating "Filtrele" button opens
 * the same filters as a slide-in drawer, so filters stay reachable.
 */
const FilterContent = ({ filters, onReset, colors, onAfterReset }) => (
  <>
    <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 custom-scrollbar">
      {filters.map((filter) => (
        <div key={filter.key} className="space-y-3">
          <h3 className={`text-xs font-bold uppercase tracking-wider ${colors.textTertiary}`}>
            {filter.label}
          </h3>

          {filter.type === 'search' && (
            <div className="relative group">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${colors.textMuted} group-focus-within:text-violet-500 transition-colors`} />
              <input
                type="text"
                placeholder={filter.placeholder}
                value={filter.value}
                onChange={(e) => filter.onChange(e.target.value)}
                className={`
                  w-full pl-10 pr-4 py-3
                  ${colors.inputBg} ${colors.inputBorder} border
                  rounded-2xl text-sm font-medium ${colors.inputText}
                  focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500
                  transition-all duration-300 shadow-inner
                `}
              />
            </div>
          )}

          {filter.type === 'select' && (
            <div className="relative">
              <select
                value={filter.value}
                onChange={(e) => filter.onChange(e.target.value)}
                className={`
                  w-full px-4 py-3 appearance-none
                  ${colors.inputBg} ${colors.inputBorder} border
                  rounded-2xl text-sm font-medium ${colors.inputText}
                  focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500
                  transition-all duration-300 shadow-sm cursor-pointer
                `}
              >
                {filter.options.map((option) => (
                  <option key={option.value} value={option.value} className={`${colors.inputBg} ${colors.inputText}`}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ChevronLeft className={`w-4 h-4 -rotate-90 ${colors.textTertiary}`} />
              </div>
            </div>
          )}

          {/* Add support for other filter types as needed */}
        </div>
      ))}
    </div>

    {onReset && (
      <div className={`p-4 border-t ${colors.border} bg-opacity-40 backdrop-blur-sm`}>
        <button
          onClick={() => {
            onReset();
            if (onAfterReset) onAfterReset();
          }}
          className="w-full py-3 rounded-xl bg-rose-500/10 hover:bg-rose-500 text-rose-500 hover:text-white border border-rose-500/20 hover:border-rose-500 transition-all duration-300 text-sm font-bold flex items-center justify-center"
        >
          <X className="w-4 h-4 mr-2" />
          Filtreleri Temizle
        </button>
      </div>
    )}
  </>
);

const FilterSidebar = ({ isOpen, onToggle, filters = [], onReset }) => {
  const { colors } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Count of active filters for the FAB badge
  const activeCount = filters.filter(
    (f) => f.value && f.value !== 'all' && f.value !== ''
  ).length;

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [mobileOpen]);

  return (
    <>
      {/* ---------- Desktop panel ---------- */}
      <div className={`
        hidden md:block
        ${isOpen ? 'md:w-72' : 'md:w-0 overflow-hidden p-0 border-0'}
        ${colors.cardBg} backdrop-blur-md bg-opacity-80
        ${colors.cardBorder} border rounded-[2rem] shadow-xl
        flex-col transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)]
        h-full
      `}>
        <div className="flex flex-col h-full">
          <div className={`p-6 border-b ${colors.border}`}>
            <div className="flex items-center justify-between">
              <h2 className={`text-xl font-black ${colors.textPrimary} tracking-tight flex items-center`}>
                <Filter className="w-5 h-5 mr-3 text-violet-500" />
                Filtreler
              </h2>
              <button
                onClick={onToggle}
                className={`p-2 rounded-xl transition-colors ${colors.textSecondary} hover:text-white hover:bg-white/10`}
                title="Kapat"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            </div>
          </div>
          <FilterContent filters={filters} onReset={onReset} colors={colors} />
        </div>
      </div>

      {/* ---------- Mobile FAB ---------- */}
      <button
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed bottom-5 right-4 z-40 flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-violet-600 text-white text-sm font-bold shadow-xl shadow-violet-600/30 active:scale-95 transition-transform"
        aria-label="Filtreleri aç"
      >
        <Filter className="w-4 h-4" />
        Filtrele
        {activeCount > 0 && (
          <span className="min-w-[20px] h-5 px-1 rounded-full bg-white text-violet-700 text-[11px] font-black flex items-center justify-center">
            {activeCount}
          </span>
        )}
      </button>

      {/* ---------- Mobile drawer ---------- */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className={`
            absolute inset-y-0 left-0 w-[85vw] max-w-[320px]
            ${colors.cardBg} border-r ${colors.cardBorder}
            flex flex-col shadow-2xl
            animate-in slide-in-from-left duration-300
          `}>
            <div className={`p-5 border-b ${colors.border} flex items-center justify-between`}>
              <h2 className={`text-lg font-black ${colors.textPrimary} tracking-tight flex items-center`}>
                <Filter className="w-5 h-5 mr-3 text-violet-500" />
                Filtreler
              </h2>
              <button
                onClick={() => setMobileOpen(false)}
                className={`p-2 rounded-xl transition-colors ${colors.textSecondary} ${colors.bgHover}`}
                aria-label="Kapat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <FilterContent
              filters={filters}
              onReset={onReset}
              colors={colors}
              onAfterReset={() => setMobileOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FilterSidebar;
